import { useState, useEffect } from 'react'
import { UserCheck, ArrowUpDown, ArrowUp, ArrowDown, FileSpreadsheet, Loader2, ChevronLeft, ChevronRight } from 'lucide-react'
import writeXlsxFile from 'write-excel-file/browser'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/shared/hooks/use-toast'
import type { EmbudoRepresentanteFila } from '../types'

const PAGE_SIZE = 20

interface Props {
  data: EmbudoRepresentanteFila[]
  isLoading?: boolean
  programaNombre?: string
}

type Columna = 'representante' | 'registrados' | 'activos' | 'pctActivos' | 'canjearon' | 'pctCanjearon'
type Direccion = 'asc' | 'desc'

function SortIcon({ columna, actual, dir }: { columna: Columna; actual: Columna; dir: Direccion }) {
  if (columna !== actual) return <ArrowUpDown className="h-3 w-3 opacity-40" />
  return dir === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />
}

function pct(n: number): string {
  return `${n.toFixed(1)}%`
}

export function TablaEmbudoRepresentante({ data, isLoading, programaNombre }: Props) {
  const { toast } = useToast()
  const [sortCol, setSortCol] = useState<Columna>('registrados')
  const [sortDir, setSortDir] = useState<Direccion>('desc')
  const [pagina, setPagina] = useState(1)
  const [exportando, setExportando] = useState(false)

  // Volver a página 1 cuando cambian los datos o el orden
  useEffect(() => { setPagina(1) }, [data, sortCol, sortDir])

  const handleSort = (col: Columna) => {
    if (col === sortCol) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))
    } else {
      setSortCol(col)
      setSortDir(col === 'representante' ? 'asc' : 'desc')
    }
  }

  const sorted = [...data].sort((a, b) => {
    const mul = sortDir === 'desc' ? -1 : 1
    if (sortCol === 'representante') return mul * a.representante.localeCompare(b.representante, 'es', { sensitivity: 'base' })
    return mul * (a[sortCol] - b[sortCol])
  })

  const totalPaginas = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const paginaActual = Math.min(pagina, totalPaginas)
  const filasPagina = sorted.slice((paginaActual - 1) * PAGE_SIZE, paginaActual * PAGE_SIZE)

  const exportarExcel = async () => {
    if (data.length === 0) return
    setExportando(true)
    try {
      const nombre = programaNombre ?? 'reporte'
      await writeXlsxFile(sorted, {
        columns: [
          { header: 'Representante', width: 32, cell: (f) => f.representante },
          { header: 'Registrados', width: 14, cell: (f) => ({ type: Number, value: f.registrados }) },
          { header: 'Activos', width: 12, cell: (f) => ({ type: Number, value: f.activos }) },
          { header: '% Activos', width: 12, cell: (f) => ({ type: Number, value: parseFloat(f.pctActivos.toFixed(1)) }) },
          { header: 'Canjearon', width: 13, cell: (f) => ({ type: Number, value: f.canjearon }) },
          { header: '% Canjearon', width: 14, cell: (f) => ({ type: Number, value: parseFloat(f.pctCanjearon.toFixed(1)) }) },
        ],
      }).toFile(`embudo-representantes-${nombre}.xlsx`)
      toast({ title: 'Descarga lista', description: `embudo-representantes-${nombre}.xlsx` })
    } catch {
      toast({ title: 'Error al exportar', description: 'No se pudo generar el archivo.', variant: 'destructive' })
    } finally {
      setExportando(false)
    }
  }

  const thCls = 'text-left py-2 px-3 text-xs font-medium text-muted-foreground whitespace-nowrap cursor-pointer select-none hover:text-foreground'
  const thNumCls = `${thCls} text-right`

  return (
    <Card className="border border-gray-300 dark:border-gray-700">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <UserCheck className="h-4 w-4 text-muted-foreground" />
          Embudo por representante
        </CardTitle>
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2.5 text-xs gap-1.5 border-gray-300 dark:border-gray-600"
          onClick={exportarExcel}
          disabled={exportando || isLoading || data.length === 0}
        >
          {exportando
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <FileSpreadsheet className="h-3.5 w-3.5" />}
          Excel
        </Button>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">Sin datos disponibles.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className={thCls} onClick={() => handleSort('representante')}>
                      <span className="inline-flex items-center gap-1">
                        Representante <SortIcon columna="representante" actual={sortCol} dir={sortDir} />
                      </span>
                    </th>
                    <th className={thNumCls} onClick={() => handleSort('registrados')}>
                      <span className="inline-flex items-center justify-end gap-1">
                        Registrados <SortIcon columna="registrados" actual={sortCol} dir={sortDir} />
                      </span>
                    </th>
                    <th className={thNumCls} onClick={() => handleSort('activos')}>
                      <span className="inline-flex items-center justify-end gap-1">
                        Activos <SortIcon columna="activos" actual={sortCol} dir={sortDir} />
                      </span>
                    </th>
                    <th className={thNumCls} onClick={() => handleSort('pctActivos')}>
                      <span className="inline-flex items-center justify-end gap-1">
                        % Activos <SortIcon columna="pctActivos" actual={sortCol} dir={sortDir} />
                      </span>
                    </th>
                    <th className={thNumCls} onClick={() => handleSort('canjearon')}>
                      <span className="inline-flex items-center justify-end gap-1">
                        Canjearon <SortIcon columna="canjearon" actual={sortCol} dir={sortDir} />
                      </span>
                    </th>
                    <th className={thNumCls} onClick={() => handleSort('pctCanjearon')}>
                      <span className="inline-flex items-center justify-end gap-1">
                        % Canjearon <SortIcon columna="pctCanjearon" actual={sortCol} dir={sortDir} />
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filasPagina.map((fila) => (
                    <tr
                      key={fila.representante}
                      className="border-b border-gray-100 dark:border-gray-800 last:border-0 hover:bg-muted/40"
                    >
                      <td className="py-2 px-3 font-medium">{fila.representante}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{fila.registrados}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{fila.activos}</td>
                      <td className="py-2 px-3 text-right tabular-nums text-muted-foreground">{pct(fila.pctActivos)}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{fila.canjearon}</td>
                      <td className="py-2 px-3 text-right tabular-nums text-muted-foreground">{pct(fila.pctCanjearon)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPaginas > 1 && (
              <div className="flex items-center justify-between pt-3 mt-1 border-t border-gray-100 dark:border-gray-800">
                <span className="text-xs text-muted-foreground">
                  {(paginaActual - 1) * PAGE_SIZE + 1}–{Math.min(paginaActual * PAGE_SIZE, sorted.length)} de {sorted.length}
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0"
                    onClick={() => setPagina((p) => Math.max(1, p - 1))}
                    disabled={paginaActual === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-xs tabular-nums px-1">
                    {paginaActual} / {totalPaginas}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0"
                    onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
                    disabled={paginaActual === totalPaginas}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
